import { Link } from '@tanstack/react-router'
import { useQuery } from '@rocicorp/zero/react'
import { Shell } from '../components/Shell.tsx'
import { computeBalance } from '../lib/balance.ts'
import { formatCents } from '../lib/format.ts'
import type { UserRow } from '../zero/schema.ts'
import { queries } from '../zero/queries.ts'
import { Require } from './guards.tsx'

function MemberBalanceRow({ member }: { member: UserRow }) {
  // Open rows only — the dashboard needs one number per member, not every
  // member's lifetime ledger.
  const [drinks] = useQuery(queries.outstandingDrinksForUser({ userId: member.id }))
  const [fees] = useQuery(queries.outstandingFeesForUser({ userId: member.id }))
  const [credits] = useQuery(queries.unusedCreditsForUser({ userId: member.id }))
  const balance = computeBalance(drinks, fees, credits)

  return (
    <Link to="/users/$userId" params={{ userId: member.id }} className="line" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="line-main">
        <div className="line-title">
          {member.displayName}
          {member.isAdmin ? <span className="pill-tag pill-tag--admin"> admin</span> : null}
        </div>
        <div className="line-sub">{member.numberOnBack != null ? `#${member.numberOnBack}` : ''}</div>
      </div>
      <span className="line-amount" style={{ color: balance > 0 ? 'var(--red)' : 'var(--green)' }}>
        {formatCents(balance)}
      </span>
    </Link>
  )
}

function AdminInner() {
  const [roster] = useQuery(queries.roster())
  return (
    <Shell title="Club balances" wide>
      <div className="admin-actions">
        <Link className="btn btn-sm" to="/admin/members">
          Members
        </Link>
        <Link className="btn btn-sm" to="/admin/drinks">
          Drink catalogue
        </Link>
        <Link className="btn btn-sm" to="/admin/bank-import">
          Reconcile bank statement
        </Link>
        <Link className="btn btn-sm" to="/admin/fees/import">
          Import fines
        </Link>
      </div>
      <div className="card">
        <h2>Members ({roster.length})</h2>
        {roster.map((m) => (
          <MemberBalanceRow key={m.id} member={m} />
        ))}
      </div>
      <p className="muted center" style={{ fontSize: 13 }}>
        You can see every member's balance because your role is <strong>admin</strong>. A member
        only sees their own — enforced by server-authorized Zero queries.
      </p>
    </Shell>
  )
}

export function AdminPage() {
  return (
    <Require roles={['admin']} showDenied>
      <AdminInner />
    </Require>
  )
}
